import { createContext, type ReactNode, useContext, useMemo, useState } from 'react'

/**
 * Whose vault this is and where its links go. The customer's Vault page and the
 * staff customer tab render the same folders and files; only these two differ.
 */
export interface VaultScope {
  orgId: string
  /** A link inside the vault, from a path like `drivers/<id>`; '' is the folder list. */
  href: (path: string) => string
  /** Staff looking in: nothing here may act on the viewer's own account, such as connecting an ELD. */
  staff?: boolean
}

/** What the provider adds: whether the scope is being edited, which the hero's Edit and the folders share. */
export interface VaultState extends VaultScope {
  editingScope: boolean
  setEditingScope: (editing: boolean) => void
}

const VaultContext = createContext<VaultState | null>(null)

export function VaultProvider({ orgId, href, staff, children }: VaultScope & { children: ReactNode }) {
  const [editingScope, setEditingScope] = useState(false)
  const value = useMemo(
    () => ({ orgId, href, staff, editingScope, setEditingScope }),
    [orgId, href, staff, editingScope]
  )
  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>
}

export function useVault(): VaultState {
  const scope = useContext(VaultContext)
  if (!scope) throw new Error('useVault must be used inside a VaultProvider')
  return scope
}
